import { memo, useState, useEffect } from 'react';
import { Handle, Position, NodeProps, useReactFlow } from 'reactflow';
import { Building2, Edit2, Trash2, MapPin, Search, X } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, Button, Input } from '@caruso/ui';
import { toast } from 'sonner';
import { useAuthStore } from '@/stores/auth-store';

interface VenueImage {
  url: string;
  isPrimary?: boolean;
}

interface Venue {
  id: string;
  name: string;
  description?: string;
  defaultPrice: number;
  pricingType: string;
  capacity?: number;
  area?: number;
  images?: VenueImage[];
}

interface VenueOfferData {
  venueId?: string;
  venueName?: string;
  venuePrice?: number;
  venueOfferMessage?: string;
  sendImages?: boolean;
}

export const VenueOfferNode = memo(({ data, id }: NodeProps<VenueOfferData>) => {
  const { deleteElements } = useReactFlow();
  const accessToken = useAuthStore((state) => state.accessToken);
  const [isEditing, setIsEditing] = useState(false);
  const [isVenueModalOpen, setIsVenueModalOpen] = useState(false);
  const [venueId, setVenueId] = useState(data.venueId || '');
  const [venueName, setVenueName] = useState(data.venueName || '');
  const [venuePrice, setVenuePrice] = useState(data.venuePrice || 0);
  const [venueOfferMessage, setVenueOfferMessage] = useState(data.venueOfferMessage || '');
  const [sendImages, setSendImages] = useState(data.sendImages ?? false);
  const [venues, setVenues] = useState<Venue[]>([]);
  const [isLoadingVenues, setIsLoadingVenues] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    if (isVenueModalOpen) {
      loadVenues();
    }
  }, [isVenueModalOpen, search]);

  const loadVenues = async () => {
    try {
      setIsLoadingVenues(true);
      const params = new URLSearchParams({
        active: 'true',
      });

      if (search) {
        params.append('search', search);
      }

      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/venues?${params}`, {
        headers: {
          'Authorization': `Bearer ${accessToken}`,
        },
      });

      if (!response.ok) throw new Error('Failed to load venues');

      const venuesList = await response.json();
      setVenues(Array.isArray(venuesList) ? venuesList : []);
    } catch (error) {
      console.error('Error loading venues:', error);
      toast.error('Erro ao carregar ambientes');
      setVenues([]);
    } finally {
      setIsLoadingVenues(false);
    }
  };

  const handleDelete = () => {
    deleteElements({ nodes: [{ id }] });
  };

  const handleSelectVenue = (venue: Venue) => {
    setVenueId(venue.id);
    setVenueName(venue.name);
    setVenuePrice(venue.defaultPrice);
    setIsVenueModalOpen(false);
  };

  const handleClearVenue = () => {
    setVenueId('');
    setVenueName('');
    setVenuePrice(0);
  };

  const handleSave = () => {
    if (!venueId || !venueName) {
      toast.error('Selecione um ambiente');
      return;
    }

    data.venueId = venueId;
    data.venueName = venueName;
    data.venuePrice = venuePrice;
    data.venueOfferMessage = venueOfferMessage;
    data.sendImages = sendImages;

    setIsEditing(false);
    toast.success('Nó de oferta de ambiente configurado');
  };

  const handleCancel = () => {
    setVenueId(data.venueId || '');
    setVenueName(data.venueName || '');
    setVenuePrice(data.venuePrice || 0);
    setVenueOfferMessage(data.venueOfferMessage || '');
    setSendImages(data.sendImages ?? false);
    setIsEditing(false);
  };

  return (
    <>
      <div className="bg-neutral-900 border-2 border-purple-400/40 rounded-lg p-4 min-w-[280px] shadow-lg">
        <Handle type="target" position={Position.Top} className="w-3 h-3 !bg-purple-400" />

        <div className="flex items-center gap-2 mb-3">
          <Building2 className="w-5 h-5 text-purple-400" />
          <span className="font-bold text-white">Oferta de Ambiente</span>
          <div className="ml-auto flex items-center gap-1">
            <button
              onClick={() => setIsEditing(true)}
              className="text-purple-400 hover:text-white transition-colors p-1"
              title="Editar oferta"
            >
              <Edit2 className="w-4 h-4" />
            </button>
            <button
              onClick={handleDelete}
              className="text-red-400 hover:text-red-300 transition-colors p-1"
              title="Deletar nó"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        </div>

        <div className="space-y-2">
          {venueName ? (
            <>
              <div className="flex items-center gap-2 p-2 bg-neutral-800 rounded">
                <MapPin className="w-4 h-4 text-purple-400" />
                <span className="flex-1 text-sm text-brand-ivory font-semibold">{venueName}</span>
              </div>
              {venueOfferMessage && (
                <div className="text-brand-ivory/80 text-sm whitespace-pre-wrap p-2 bg-neutral-800 rounded">
                  {venueOfferMessage}
                </div>
              )}
            </>
          ) : (
            <div className="text-brand-ivory/50 text-sm text-center py-3">
              Clique para selecionar um ambiente
            </div>
          )}
        </div>

        <Handle type="source" position={Position.Bottom} className="w-3 h-3 !bg-purple-400" />
      </div>

      <Dialog open={isEditing} onOpenChange={setIsEditing}>
        <DialogContent className="bg-neutral-900 border-brand-gold/20 text-white max-w-4xl w-[90vw]">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Building2 className="w-5 h-5 text-purple-400" />
              Configurar Oferta de Ambiente
            </DialogTitle>
            <DialogDescription className="text-brand-ivory/70">
              Selecione um ambiente e personalize a mensagem
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            {/* Venue Selector */}
            <div>
              <label className="block text-sm font-medium text-brand-ivory mb-2">
                Ambiente *
              </label>
              {venueName ? (
                <div className="flex items-center gap-2 p-3 bg-neutral-800/50 border border-neutral-700 rounded-lg">
                  <MapPin className="w-5 h-5 text-purple-400 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-white truncate">{venueName}</p>
                    <p className="text-xs text-brand-ivory/70">R$ {venuePrice.toFixed(2)}</p>
                  </div>
                  <button
                    onClick={handleClearVenue}
                    className="text-red-400 hover:text-red-300 transition-colors p-1"
                    title="Remover ambiente"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <Button
                  onClick={() => setIsVenueModalOpen(true)}
                  variant="outline"
                  className="w-full justify-start border-neutral-700 text-white hover:bg-neutral-800"
                >
                  <Search className="w-4 h-4 mr-2" />
                  Pesquisar ambiente
                </Button>
              )}
            </div>

            {/* Send Images Checkbox */}
            <div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={sendImages}
                  onChange={(e) => setSendImages(e.target.checked)}
                  className="w-4 h-4 text-purple-400 bg-neutral-800 border-neutral-700 rounded focus:ring-purple-400 focus:ring-2"
                />
                <span className="text-sm font-medium text-brand-ivory">
                  Enviar imagens do ambiente (até 3 fotos)
                </span>
              </label>
              <p className="text-xs text-brand-ivory/50 mt-1 ml-6">
                As imagens cadastradas no ambiente serão enviadas antes da mensagem
              </p>
            </div>

            {/* Custom Message */}
            <div>
              <label className="block text-sm font-medium text-brand-ivory mb-2">
                Mensagem com Template (opcional)
              </label>
              <textarea
                value={venueOfferMessage}
                onChange={(e) => setVenueOfferMessage(e.target.value)}
                className="w-full p-4 bg-neutral-800/50 border border-neutral-700 text-white placeholder:text-neutral-500 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-purple-400/50 font-mono text-base"
                rows={8}
                placeholder="Exemplo: Temos um ambiente maravilhoso para o seu evento! {{nome_ambiente}} está disponível. Veja os detalhes: {{link_ambiente}}"
              />
              <div className="mt-3 space-y-2">
                <p className="text-sm font-semibold text-purple-400">Variáveis disponíveis:</p>
                <div className="grid grid-cols-2 gap-3">
                  <div className="text-sm text-brand-ivory/70 bg-neutral-800 px-3 py-2 rounded font-mono">
                    {'{{nome_ambiente}}'} - Nome do ambiente
                  </div>
                  <div className="text-sm text-brand-ivory/70 bg-neutral-800 px-3 py-2 rounded font-mono">
                    {'{{preco_ambiente}}'} - Preço formatado
                  </div>
                  <div className="text-sm text-brand-ivory/70 bg-neutral-800 px-3 py-2 rounded font-mono">
                    {'{{link_ambiente}}'} - Link para detalhes
                  </div>
                  <div className="text-sm text-brand-ivory/70 bg-neutral-800 px-3 py-2 rounded font-mono">
                    {'{{nome_cliente}}'} - Nome do cliente
                  </div>
                </div>
              </div>
            </div>

            <div className="flex gap-2 justify-end pt-2">
              <Button
                onClick={handleCancel}
                variant="outline"
                className="border-neutral-700 text-white hover:bg-neutral-800"
              >
                Cancelar
              </Button>
              <Button
                onClick={handleSave}
                className="bg-purple-400 hover:bg-purple-500 text-white font-semibold"
              >
                Salvar
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal de Pesquisa de Ambientes */}
      <Dialog open={isVenueModalOpen} onOpenChange={setIsVenueModalOpen}>
        <DialogContent className="bg-neutral-900 border-brand-gold/20 text-white w-[95vw] max-w-[3000px] max-h-[85vh] flex flex-col p-0 gap-0">
          <div className="p-6 pb-4">
            <DialogHeader>
              <DialogTitle className="text-white flex items-center gap-2">
                <Building2 className="w-5 h-5 text-purple-400" />
                Selecionar Ambiente
              </DialogTitle>
              <DialogDescription className="text-brand-ivory/70">
                Pesquise e selecione o ambiente para oferta
              </DialogDescription>
            </DialogHeader>

            {/* Search Input */}
            <div className="relative mt-4">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-brand-ivory/50" />
              <Input
                placeholder="Pesquisar ambientes..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className="pl-10 bg-neutral-800/50 border-neutral-700 text-white placeholder:text-neutral-500"
              />
            </div>
          </div>

          {/* Venues List - Scrollable */}
          <div className="flex-1 overflow-y-auto px-6 min-h-0">
            <div className="space-y-2 pb-4">
              {isLoadingVenues ? (
                <div className="text-center py-8 text-brand-ivory/50">
                  Carregando ambientes...
                </div>
              ) : venues.length === 0 ? (
                <div className="text-center py-8 text-brand-ivory/50">
                  Nenhum ambiente encontrado
                </div>
              ) : (
                venues.map((venue) => {
                  const primaryImage = venue.images?.find(img => img.isPrimary) || venue.images?.[0];
                  return (
                    <button
                      key={venue.id}
                      onClick={() => handleSelectVenue(venue)}
                      className="w-full flex items-center gap-4 p-4 bg-neutral-800/50 hover:bg-neutral-800 border border-neutral-700 hover:border-purple-400/50 rounded-lg transition-all text-left group"
                    >
                      {primaryImage ? (
                        <div className="w-16 h-16 flex-shrink-0 rounded border border-neutral-700 overflow-hidden bg-neutral-700">
                          <img
                            src={primaryImage.url}
                            alt={venue.name}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              e.currentTarget.style.display = 'none';
                              const placeholder = e.currentTarget.nextElementSibling as HTMLElement;
                              if (placeholder) placeholder.style.display = 'flex';
                            }}
                          />
                          <div className="w-full h-full hidden items-center justify-center">
                            <MapPin className="w-7 h-7 text-purple-400/50" />
                          </div>
                        </div>
                      ) : (
                        <div className="w-16 h-16 flex items-center justify-center bg-neutral-700 rounded border border-neutral-600 flex-shrink-0">
                          <MapPin className="w-7 h-7 text-purple-400/50" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-base font-medium text-white group-hover:text-purple-400 transition-colors">
                          {venue.name}
                        </p>
                        <div className="flex items-center gap-3 mt-1.5">
                          <p className="text-sm text-purple-400 font-semibold">
                            R$ {venue.defaultPrice.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            <span className="text-xs text-brand-ivory/50 ml-1">
                              /{venue.pricingType === 'daily' ? 'dia' : 'hora'}
                            </span>
                          </p>
                          {venue.capacity && (
                            <span className="text-sm text-brand-ivory/50">
                              • {venue.capacity} pessoas
                            </span>
                          )}
                        </div>
                      </div>
                    </button>
                  );
                })
              )}
            </div>
          </div>

          {/* Footer - Fixed */}
          <div className="p-6 pt-4 border-t border-neutral-700 flex justify-end">
            <Button
              onClick={() => setIsVenueModalOpen(false)}
              variant="outline"
              className="border-neutral-700 text-white hover:bg-neutral-800"
            >
              Cancelar
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
});

VenueOfferNode.displayName = 'VenueOfferNode';
